import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, ArrowRight } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO, { createServiceSchema } from '../components/SEO';

const accessories = [
  { name: "Aluminium Trays", description: "Custom-built aluminium trays for utes and trucks" },
  { name: "Steel Trays", description: "Heavy-duty steel trays for commercial use" },
  { name: "Canopies", description: "Weather-resistant canopies in various styles" },
  { name: "Bull Bars", description: "Protective bull bars for all vehicle types" },
  { name: "Side Steps", description: "Durable side steps for easy access" },
  { name: "Roof Racks", description: "Versatile roof rack systems" },
  { name: "Tow Bars", description: "Heavy-duty tow bar installations" },
  { name: "Toolboxes", description: "Secure toolbox solutions" }
];

const VehicleAccessoriesPage = () => {
  const { isDark } = useTheme();

  const serviceSchema = createServiceSchema(
    'Vehicle Accessories - Trays, Canopies & Bull Bars',
    'Quality vehicle accessories including aluminium trays, steel trays, canopies, bull bars, side steps, roof racks, and tow bars in Murray Bridge, South Australia.',
    'https://www.jprcustoms.com.au/vehicle-accessories'
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="Vehicle Accessories - Trays, Canopies & Bull Bars"
        description="Quality vehicle accessories in Murray Bridge SA. Aluminium trays, steel trays, canopies, bull bars, side steps, roof racks, tow bars & toolboxes. Custom fabrication available. Call +61 417 119 544."
        keywords="vehicle accessories, ute trays, bull bars Murray Bridge, canopies, side steps, roof racks, tow bars, toolboxes, 4x4 accessories"
        canonical="/vehicle-accessories"
        structuredData={serviceSchema}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80)` }}
          >
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-neutral-950/60' : 'bg-gradient-to-r from-white/95 via-white/85 to-white/70'}`} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 pt-44">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              Vehicle Accessories<br />
              <span className="text-lime-500">Trays, Canopies & Bull Bars</span>
            </h1>
            <p className={`text-xl max-w-2xl mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
              Quality accessories to enhance your vehicle's functionality and style.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              <Phone className="w-5 h-5" />
              Get a Free Quote
            </a>
          </div>
        </section>

        {/* Content */}
        <section className={`py-20 ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-lime-500 mb-6">Premium Vehicle Accessories</h2>
              <p className={`max-w-3xl mx-auto leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                Whether you need a sturdy tray for your work vehicle, a protective bull bar for the outback, or a sleek canopy for camping adventures, we've got you covered.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {accessories.map((item, index) => (
                <div 
                  key={index} 
                  className={`p-6 rounded-xl transition-colors group ${isDark ? 'bg-neutral-800 hover:bg-neutral-750' : 'bg-white hover:bg-neutral-100 shadow-sm'}`}
                >
                  <h3 className="text-lg font-semibold text-lime-500 mb-2 group-hover:text-lime-400 transition-colors">
                    {item.name}
                  </h3>
                  <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>{item.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <img 
                src="https://images.unsplash.com/photo-1544829099-b9a0c07fad1a?w=800&q=80" 
                alt="Vehicle accessories"
                className="w-full h-80 object-cover rounded-xl"
              />
              <div className="flex flex-col justify-center">
                <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Custom Solutions</h3>
                <p className={`mb-6 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  Every vehicle and every customer is unique. That's why we offer custom fabrication services tailored to your specific needs.
                </p>
                <ul className="space-y-3">
                  <li className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                    <Check className="w-5 h-5 text-lime-500" />
                    <span>Custom design and fabrication</span>
                  </li>
                  <li className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                    <Check className="w-5 h-5 text-lime-500" />
                    <span>Professional installation</span>
                  </li>
                  <li className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                    <Check className="w-5 h-5 text-lime-500" />
                    <span>Quality materials guaranteed</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Need Custom Accessories?</h2>
            <p className={`mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>Contact us to discuss your requirements.</p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              Call {companyInfo.phone}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VehicleAccessoriesPage;
